/**
 * Adobe Tracking Plugin
 */

import { trackApplicationInteraction } from './trackApplicationInteraction';
import { trackFormSubmitSuccess } from './trackFormSubmitSuccess';
import { trackFormView } from './trackFormView';
import { trackPageView } from './trackPageView';
import { trackSiteSearch } from './trackSiteSearch';
import {
    ADOBE_TRACKING_ENABLED,
    ADOBE_TRACKING_URL,
    logDebug,
    logError,
    setDataLayerInitialized,
} from './utils';

import type { ApplicationInteractionParams } from './trackApplicationInteraction';
import type { FormSubmitSuccessParams } from './trackFormSubmitSuccess';
import type { FormViewParams } from './trackFormView';
import type { SiteSearchParams } from './trackSiteSearch';

export interface AdobeTrackingPluginConfig {
    enabled?: boolean;
    scriptUrl?: string;
}

export default class AdobeTrackingPlugin {
    private _isLoaded = false;
    private _error: Error | null = null;
    private readonly _config: AdobeTrackingPluginConfig;
    private readonly _readyPromise: Promise<void>;
    private _readyResolve!: () => void;

    constructor(config: AdobeTrackingPluginConfig = {}) {
        this._config = {
            enabled: config.enabled ?? ADOBE_TRACKING_ENABLED,
            scriptUrl: config.scriptUrl ?? ADOBE_TRACKING_URL,
        };

        this._readyPromise = new Promise<void>((resolve) => {
            this._readyResolve = resolve;
        });

        this._loadScript();
    }

    /**
     * Returns a promise that resolves when the plugin is ready.
     */
    public ready(): Promise<void> {
        return this._readyPromise;
    }

    private _loadScript(): void {
        if (!this._config.enabled) {
            logDebug('Adobe Tracking is disabled');
            this._readyResolve();
            return;
        }

        if (!this._config.scriptUrl) {
            this._error = new Error('No script URL provided');
            logError('No script URL provided');
            this._readyResolve();
            return;
        }

        const existingScript = document.querySelector(`script[src="${this._config.scriptUrl}"]`);
        if (existingScript) {
            logDebug('Script already exists in DOM');
            this._onLoaded();
            return;
        }

        logDebug('Loading Adobe Tracking script...', this._config.scriptUrl);

        const script = document.createElement('script');
        script.src = this._config.scriptUrl;
        script.async = true;
        script.type = 'text/javascript';
        script.onload = () => this._onLoaded();
        script.onerror = () => {
            this._error = new Error('Failed to load Adobe Tracking script');
            logError('Script loading failed', this._error);
            this._readyResolve();
        };

        document.head.appendChild(script);
    }

    private _onLoaded(): void {
        if (this._isLoaded) return;

        this._isLoaded = true;
        setDataLayerInitialized();
        logDebug('Adobe Tracking initialized');
        this._readyResolve();
    }

    // ============================================================================
    // Getters
    // ============================================================================

    public get isEnabled(): boolean {
        return this._config.enabled ?? false;
    }

    public get isLoaded(): boolean {
        return this._isLoaded;
    }

    public get error(): Error | null {
        return this._error;
    }

    // ============================================================================
    // Tracking Methods (all return Promise, wait for ready if needed)
    // ============================================================================

    private readonly _track = async (action: () => void | Promise<void>): Promise<void> => {
        await this._readyPromise;
        if (!this._isLoaded) {
            return;
        }
        await action();
    };

    public readonly trackPageView = (): Promise<void> => {
        return this._track(() => trackPageView());
    };

    public readonly trackSiteSearch = (params: SiteSearchParams): Promise<void> => {
        return this._track(() => trackSiteSearch(params));
    };

    public readonly trackApplicationInteraction = (params: ApplicationInteractionParams): Promise<void> => {
        return this._track(() => trackApplicationInteraction(params));
    };

    public readonly trackFormView = (params: FormViewParams): Promise<void> => {
        return this._track(() => trackFormView(params));
    };

    public readonly trackFormSubmitSuccess = (params: FormSubmitSuccessParams): Promise<void> => {
        return this._track(() => trackFormSubmitSuccess(params));
    };
}
